// src/user/Apps.js
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Grid,
  Divider,
  Container,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import {
  PhoneAndroid,
  Apple,
  OpenInNew,
  VideoLibrary,
  SportsEsports,
  MoreHoriz,
} from '@mui/icons-material';

function Apps() {
  // Main App - Dreamey Vids
  const dreameyVidsAndroid = {
    id: 'dreamey-vids-android',
    name: 'Dreamey Vids',
    description: 'Watch videos, explore destinations, sports highlights, and religious content',
    icon: '/dreamey-vids-icon.png',
    iconType: 'image',
    url: 'https://play.google.com/store/apps/details?id=com.dreamey.app&pcampaignid=web_share',
    color: '#667eea',
    platform: 'android',
  };

  const dreameyVidsIOS = {
    id: 'dreamey-vids-ios',
    name: 'Dreamey Vids',
    description: 'Watch videos, explore destinations, sports highlights, and religious content',
    icon: '/dreamey-vids-icon.png',
    iconType: 'image',
    url: 'https://apps.apple.com/pk/app/dreamey-vids/id6755639217',
    color: '#667eea',
    platform: 'ios',
  };

  // Other Apps - Dreamey Ludo
  const dreameyLudoAndroid = {
    id: 'dreamey-ludo-android',
    name: 'Dreamey Ludo',
    description: 'Classic Ludo board game with multiple game modes and beautiful design',
    icon: '/dreamey-ludo-icon.png',
    iconType: 'image',
    url: 'https://play.google.com/store/apps/details?id=com.dreamey.ludo&pcampaignid=web_share',
    color: '#f5576c',
    platform: 'android',
  };

  const dreameyLudoIOS = {
    id: 'dreamey-ludo-ios',
    name: 'Dreamey Ludo',
    description: 'Classic Ludo board game with multiple game modes and beautiful design',
    icon: '/dreamey-ludo-icon.png',
    iconType: 'image',
    url: 'https://apps.apple.com/pk/app/dreamey-ludo/id6756227591',
    color: '#f5576c',
    platform: 'ios',
  };

  const handleAppClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const AppCard = ({ app, platform }) => (
    <Card
      elevation={2}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
          borderColor: app.color || 'primary.main',
        },
      }}
      onClick={() => handleAppClick(app.url)}
    >
      <CardContent sx={{ px: { xs: '12px', sm: '16px', md: '24px' }, py: { xs: '8px', sm: '12px', md: '16px' }, '&:last-child': { pb: { xs: '8px', sm: '12px', md: '16px' } } }}>
        <Box>
          {/* First Row: App Name */}
          <Typography 
            variant="h6" 
            component="h3" 
            sx={{ 
              fontWeight: 600, 
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
              lineHeight: 1.2,
              textAlign: 'center',
              mb: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {app.name}
          </Typography>

          {/* Second Row: App Icon, Platform Tag, and Link Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5, md: 2 } }}>
            {/* App Icon */}
            <Box
              sx={{
                width: { xs: 40, sm: 48, md: 64 },
                height: { xs: 40, sm: 48, md: 64 },
                borderRadius: 2,
                bgcolor: app.iconType === 'image' ? 'transparent' : (app.color || 'primary.main'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {app.iconType === 'image' ? (
                <img
                  src={app.icon}
                  alt={`${app.name} icon`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                app.icon
              )}
            </Box>

            {/* Platform Tag */}
            <Chip
              label={platform === 'android' ? 'Android' : 'iOS'}
              size="small"
              icon={platform === 'android' ? <PhoneAndroid /> : <Apple />}
              sx={{
                height: { xs: 20, sm: 24, md: 28 },
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                '& .MuiChip-icon': {
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                },
              }}
            />

            {/* Link Button - Hidden */}
            <Box sx={{ flex: 1 }} />
            <IconButton
              size="small"
              sx={{
                display: 'none',
                color: 'text.secondary',
                padding: { xs: '4px', sm: '8px' },
                '&:hover': {
                  color: app.color || 'primary.main',
                },
                '& svg': {
                  fontSize: { xs: '16px', sm: '18px', md: '24px' },
                },
              }}
            >
              <OpenInNew />
            </IconButton>
          </Box>

          {/* Description - Hidden on mobile, shown on desktop */}
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              lineHeight: 1.6,
              fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
              display: { xs: 'none', sm: 'block' },
              mt: { xs: 0, sm: 1.5, md: 2 },
            }}
          >
            {app.description}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const PlatformSection = ({ platform, apps, title, icon, gradient }) => (
    <Box>
      {/* Platform Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5, md: 2 },
          mb: { xs: 2, sm: 2.5, md: 3 },
          p: { xs: 1, sm: 1.5, md: 2 },
          borderRadius: 2,
          background: gradient,
          color: 'white',
        }}
      >
        <Box
          sx={{
            width: { xs: 40, sm: 48, md: 56 },
            height: { xs: 40, sm: 48, md: 56 },
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            sx={{ 
              fontWeight: 700, 
              mb: 0.5,
              fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.5rem' },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              opacity: 0.9,
              fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' },
            }}
          >
            {apps.length} {apps.length === 1 ? 'app available' : 'apps available'}
          </Typography>
        </Box>
      </Box>

      {/* Apps Grid */}
      {apps.length > 0 ? (
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
          {apps.map((app) => (
            <Grid item xs={12} key={app.id}>
              <AppCard app={app} platform={platform} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '2px dashed',
            borderColor: 'divider',
          }}
        >
          <MoreHoriz sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
            More apps coming soon
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We're working on bringing our apps to {platform === 'android' ? 'Android' : 'iOS'}.
            Stay tuned for updates!
          </Typography>
        </Paper>
      )}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 5 }, textAlign: 'center' }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            whiteSpace: { xs: 'nowrap', sm: 'normal' },
            fontSize: { xs: '1.25rem', sm: '2.125rem', md: '3rem' },
            overflow: { xs: 'hidden', sm: 'visible' },
            textOverflow: { xs: 'ellipsis', sm: 'clip' },
          }}
        >
          Download Our Other Apps
        </Typography>
      </Box>

      {/* Main App - Dreamey Vids Section */}
      {/* Hidden per user request */}
      {false && (
      <Box sx={{ mb: { xs: 2, sm: 6 } }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 3,
            fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
            textAlign: 'center',
          }}
        >
          Our Project
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1, sm: 2, md: 4 }, justifyContent: 'center' }}>
          {/* Android - Left side */}
          <Box sx={{ flex: '0 1 auto', minWidth: { xs: '140px', sm: 'auto' } }}>
            <AppCard app={dreameyVidsAndroid} platform="android" />
          </Box>

          {/* Vertical Divider */}
          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              mx: { xs: 0.5, sm: 1, md: 2 }
            }} 
          />

          {/* iOS - Right side */}
          <Box sx={{ flex: '0 1 auto', minWidth: { xs: '140px', sm: 'auto' } }}>
            <AppCard app={dreameyVidsIOS} platform="ios" />
          </Box>
        </Box>
      </Box>
      )}

      {/* Other Apps Section */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: { xs: 1.5, sm: 3 },
            fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
            textAlign: 'center',
          }}
        >
          Please Support Us
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 1, sm: 2, md: 4 }, justifyContent: 'center' }}>
          {/* Android - Left side */}
          <Box sx={{ flex: '0 1 auto', minWidth: { xs: '140px', sm: 'auto' } }}>
            <AppCard app={dreameyLudoAndroid} platform="android" />
          </Box>

          {/* Vertical Divider */}
          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ 
              mx: { xs: 0.5, sm: 1, md: 2 }
            }} 
          />

          {/* iOS - Right side */}
          <Box sx={{ flex: '0 1 auto', minWidth: { xs: '140px', sm: 'auto' } }}>
            <AppCard app={dreameyLudoIOS} platform="ios" />
          </Box>
        </Box>
      </Box>

      {/* Additional Info Section */}
      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            If you encounter any issues downloading or installing our apps, please contact our support team.
            All apps are free to download and use.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
}

export default Apps;
